import { Component, effect, inject, input, output, signal, ViewChild} from '@angular/core';
import { MessageService } from 'primeng/api';
import { FileBeforeUploadEvent, FileSelectEvent, FileUpload, FileUploadEvent } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface UploadEvent {
  originalEvent: any;
  files: File[];
}

export interface OptionsComponent {
  name: string;
  directory: string;
  multiple: boolean;
  accept: string;
  maxFileSize: number;
  mode: 'advanced' | 'basic' | undefined;
  url: string;
  uploaded: boolean;
  uploadedFilesNames?: string[];
  uploadFunction?: (event: FileUploadEvent) => void | null;
}

@Component({
  selector: 'app-upload-file',
  imports: [
    FileUpload,
    ToastModule,
    CommonModule,
    Dialog
],
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.scss',
  standalone: true,
  providers: [ MessageService ],
})
export class UploadFileComponent  {

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  showUploadDialog = input.required<boolean>();

  options = input.required<OptionsComponent>();

  files = signal<File[]>([]);

  nameFile = signal<string[]>([]);

  hasInvalidFileNames = signal<boolean>(false);

  invalidFileNames = signal<string[]>([]);

  title = input<String>('Subir Archivo');

  eventUpload = output<UploadEvent>();

  eventClose = output<void>();

  namesFilesOutput = output<string[]>();

  http = inject(HttpClient);

  messageService = inject(MessageService);

  constructor(){
    effect(() => {
      const currentNameFile = this.nameFile();

      this.namesFilesOutput.emit(currentNameFile);
    });
  }
 
  onUpload(event: FileUploadEvent): void {
    // Bloquear subida si hay nombres inválidos
    if (this.hasInvalidFileNames()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Subida bloqueada',
        detail: 'Corrige los nombres de archivo antes de subir',
        life: 3000
      });
      return;
    }

    for (const file of event.files) {
      this.files().push(file);
    }
    
    this.messageService.add({severity: 'info', summary: 'Archivo(s) Subido', detail: ''});

    this.eventUpload.emit({ originalEvent: event.originalEvent, files: event.files });
  }

  onBeforeUpload(event: FileBeforeUploadEvent): void {
    console.log('Before upload event:', event.formData.get('file'));
  }

  onSelect(event: FileSelectEvent): void {
    
    if (event.currentFiles && event.currentFiles.length > 0) {

      const fileNames = event.currentFiles.map(file => file.name);
      const invalidFiles = this.validateFileNames(fileNames);
    
      if (invalidFiles.length > 0) {

        this.hasInvalidFileNames.set(true);
        this.invalidFileNames.set(invalidFiles);
        this.nameFile.set([]);
        this.fileUpload.clear();

        this.messageService.add({
          severity: 'error',
          summary: 'Nombres de archivo inválidos',
          detail: 'Los archivos no deben contener espacios ni caracteres especiales',
          life: 5000
        });
      } else {
        this.hasInvalidFileNames.set(false);
        this.invalidFileNames.set([]);
        this.nameFile.set(fileNames);
      }

    } else {
        this.nameFile.set([]);
        this.hasInvalidFileNames.set(false);
        this.invalidFileNames.set([]);
    }
  }

  /**
   * Valida que los nombres de archivo solo contengan caracteres alfanuméricos, guiones y puntos
   * @param fileNames Array de nombres de archivos
   * @returns Array de nombres inválidos
   */
  private validateFileNames(fileNames: string[]): string[] {
    // Regex: solo permite letras, números, guiones, guiones bajos y puntos
    // No permite espacios ni caracteres especiales como @, #, %, etc.
    const validFileNameRegex = /^[a-zA-Z0-9._-]+$/;
    
    return fileNames.filter(fileName => !validFileNameRegex.test(fileName));
  }

  checkDirectoryIsEmpty() {

    const params = new HttpParams()
      .set('p1', 'upload')
      .set('p2', 'check_directory')
      .set('module', this.options().directory)
      .set('client', this.options().name);

    this.http.get(environment.serverUrl + 'ws/index.php', { params }).subscribe(
      (response: any) => {
        if (response.statusCode === 200 ) {
          if(response.data) {
            this.options().uploaded = true;
            
            response.data.forEach((fileName: string) => {
              if (this.nameFile().length > 0) {
                this.nameFile.set([...this.nameFile(), fileName]);
              } else {
                this.nameFile.set([fileName]);
              }
            });
          }
        }
      },
      (error) => {
        console.error('Error al verificar el archivo en el servidor:', error);
      }
    );
  }

  openWindowUploadHelp( filename: string ): void {
    window.open(environment.serverUrl + 'uploads/' + this.options().directory + '/' + this.options().name + '/' + filename, '_blank');
  }
}
